import React, { useState, useRef, useEffect, useCallback } from 'react';
import { noBusinessesMessage } from '../utils/searchMessages';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import BugReportForm from './BugReportForm';
import ImpersonationBanner from './ImpersonationBanner';
import { useAuth } from '../contexts/AuthContext';
import { useCredits } from '../contexts/CreditsContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import { searchAPI, emailVerificationAPI } from '../services/api';
import './Layout.css';

const APP_NAME = 'Outreach Engine';
const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || 'support@outreachengine.com';
const APP_VERSION = process.env.REACT_APP_APP_VERSION || process.env.REACT_APP_VERSION || 'dev';

function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [bugReportFormOpen, setBugReportFormOpen] = useState(false);
  const [notification, setNotification] = useState(null); // { message, type: 'success'|'error'|'info' }
  const toastTimeoutRef = useRef(null);
  const notificationTimeoutRef = useRef(null);
  const searchPollRef = useRef(null);
  const location = useLocation();
  const { user } = useAuth();
  const { refreshCredits } = useCredits();
  const { refresh: refreshOnboarding } = useOnboarding();

  // Request browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ message, type });
    if (notificationTimeoutRef.current) clearTimeout(notificationTimeoutRef.current);
    notificationTimeoutRef.current = setTimeout(() => setNotification(null), 8000);

    // Also fire a native browser/desktop notification if permission granted
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Outreach Engine', {
        body: message,
        icon: '/favicon.ico',
      });
    }
  }, []);

  // Poll for any background jobs (search or email verification) stored in localStorage
  useEffect(() => {
    const checkPendingJobs = async () => {
      // --- Search job ---
      const rawSearch = localStorage.getItem('pending_search_job');
      if (rawSearch && location.pathname !== '/search') {
        let jobData;
        try { jobData = JSON.parse(rawSearch); } catch { localStorage.removeItem('pending_search_job'); }

        if (jobData) {
          // Expire after 15 minutes
          if (Date.now() - jobData.startedAt > 900000) {
            localStorage.removeItem('pending_search_job');
          } else {
            try {
              const jobResponse = await searchAPI.getJobStatus(jobData.jobId);
              const job = jobResponse.data.job;
              if (job.status === 'completed') {
                localStorage.removeItem('pending_search_job');
                const result = job.result || {};
                const creditsSpent = result.credits_spent || 0;
                const unsaved = result.unsaved_for_credits || 0;
                let creditNote = ` — ${creditsSpent} credit${creditsSpent === 1 ? '' : 's'} used`;
                if (unsaved > 0) creditNote += `. ${unsaved} prospect${unsaved === 1 ? '' : 's'} not saved because you ran out of credits`;
                refreshCredits();
                refreshOnboarding();
                if ((result.buyers_found || 0) === 0) {
                  showNotification(noBusinessesMessage(jobData.industry, jobData.location), 'error');
                } else {
                  showNotification(
                    `Search complete! Found ${result.buyers_found || 0} businesses, added ${result.buyers_added || 0} new prospects in ${jobData.location}${creditNote}.`,
                    'success'
                  );
                }
              } else if (job.status === 'failed') {
                localStorage.removeItem('pending_search_job');
                showNotification(`Search failed: ${job.error_message || 'Unknown error'}`, 'error');
              }
            } catch (e) { /* silently ignore poll errors */ }
          }
        }
      }

      // --- Verification job ---
      const rawVerify = localStorage.getItem('pending_verification_job');
      if (rawVerify) {
        let jobData;
        try { jobData = JSON.parse(rawVerify); } catch { localStorage.removeItem('pending_verification_job'); }

        if (jobData) {
          // Expire after 30 minutes
          if (Date.now() - jobData.startedAt > 1800000) {
            localStorage.removeItem('pending_verification_job');
          } else {
            try {
              const jobResponse = await emailVerificationAPI.getJobStatus(jobData.jobId);
              const job = jobResponse.data.job;
              if (job.status === 'completed') {
                localStorage.removeItem('pending_verification_job');
                const used = job.credits_used || 0;
                const usedNote = used > 0 ? ` — ${used} credit${used === 1 ? '' : 's'} used` : '';
                refreshCredits();
                showNotification(`Email verification complete!${usedNote}`, 'success');
              } else if (job.status === 'failed') {
                localStorage.removeItem('pending_verification_job');
                refreshCredits();
                showNotification(`Email verification failed: ${job.error || 'Unknown error'}`, 'error');
              } else if (job.status === 'stopped_no_credits') {
                localStorage.removeItem('pending_verification_job');
                refreshCredits();
                showNotification(
                  `Verification stopped: out of credits — ${job.verified || 0} checked, ${job.skipped_recent || 0} skipped (already verified). Buy more credits on the Prospects page.`,
                  'error'
                );
              }
            } catch (e) { /* silently ignore poll errors */ }
          }
        }
      }
    };

    // Poll every 5 seconds
    searchPollRef.current = setInterval(checkPendingJobs, 5000);
    checkPendingJobs(); // Run immediately on mount/route change

    return () => {
      if (searchPollRef.current) clearInterval(searchPollRef.current);
    };
  }, [location.pathname, showNotification, refreshCredits, refreshOnboarding]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleReportBug = () => {
    setBugReportFormOpen(true);
  };

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
      if (notificationTimeoutRef.current) clearTimeout(notificationTimeoutRef.current);
    };
  }, []);

  const handleToggleSidebarCollapse = () => {
    setSidebarCollapsed(prev => !prev);
  };

  const currentYear = new Date().getFullYear();

  return (
    <div
      className={`layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${sidebarOpen ? 'sidebar-open' : ''} ${user?.impersonating ? 'layout--viewing' : ''}`}
    >
      {user?.impersonating && <ImpersonationBanner impersonating={user.impersonating} email={user.email} />}
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggleCollapse={handleToggleSidebarCollapse}
        onReportBug={handleReportBug}
        mobileOpen={sidebarOpen}
        onCloseMobile={() => setSidebarOpen(false)}
      />

      <div className="main-content">
        <header className="top-header">
          <div className="header-container">
            <button
              className="mobile-menu-toggle"
              onClick={toggleSidebar}
              aria-label="Toggle menu"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 12h18M3 6h18M3 18h18" />
              </svg>
            </button>

            <div className="header-actions">
              <div className="header-item">
                <span className="header-label">Status</span>
                <span className="status-indicator online">Online</span>
              </div>

              <div className="header-item">
                <span className="header-label">Last Sync</span>
                <span className="sync-time">Just now</span>
              </div>
            </div>
          </div>
        </header>

        <main className="content">
          <Outlet />
        </main>

        <footer className="app-footer">
          <div className="footer-content">
            <p>&copy; {currentYear} {APP_NAME}. Enterprise prospect management platform.</p>
          </div>
          {toastVisible && (
            <div className="bug-toast">
              Thanks! Your email client has been opened so you can send the report.
            </div>
          )}
        </footer>

        {/* Global notification toast — shows search completion etc across all pages */}
        {notification && (
          <div className={`global-notification global-notification--${notification.type}`}>
            <span>{notification.message}</span>
            <button onClick={() => setNotification(null)} className="global-notification__close">✕</button>
          </div>
        )}
      </div>

      {bugReportFormOpen && (
        <BugReportForm onClose={() => setBugReportFormOpen(false)} />
      )}
    </div>
  );
}

export default Layout;
